import { useMutation, useQuery } from '@tanstack/react-query';
import { ErroreApi, chiama } from './cliente.js';
import { t } from '../i18n/lingua.js';

/**
 * L'importazione degli ordini da Excel, dalla parte di chi la usa.
 *
 * Il file si carica una volta sola e resta al servizio con un gettone: si
 * cambia foglio, si cambia mappatura, si guarda l'anteprima quante volte
 * serve senza rispedire due megabyte a ogni ripensamento.
 */

export interface CampoMappabile {
  chiave: string;
  etichetta: string;
  aiuto: string;
  obbligatorio: boolean;
}

export interface Colonna {
  lettera: string;
  indice: number;
  titolo: string;
  esempi: string[];
}

export interface Foglio {
  nome: string;
  righe: number;
  colonne: Colonna[];
  anteprima: string[][];
}

export type Mappatura = Record<string, string | null>;

export interface EsitoCaricamento {
  token: string;
  fileName: string;
  sheets: Foglio[];
  suggestedSheet: string;
  mapping: Mappatura;
  /** Vero quando la mappatura viene dall'ultima importazione di questo cliente. */
  remembered: boolean;
  /** Stabilimento del foglio → destinazione, da quello che si è detto in passato. */
  plants: Record<string, number>;
  missing: string[];
}

export interface ProblemaRiga {
  riga: number;
  gravita: 'bloccante' | 'avvertimento';
  testo: string;
}

export interface RigaAnteprima {
  riga: number;
  articleCode: string;
  articleId: number | null;
  articleDescription: string | null;
  descrizioneFoglio: string | null;
  quantity: number | null;
  priceExw: string | null;
  costDdp: string | null;
  fromDestination: boolean;
  prezzoNelFoglio: number | null;
  /** Il prezzo del foglio non è quello del listino: vale il listino. */
  prezzoDiverso: boolean;
  problemi: ProblemaRiga[];
}

export interface OrdineAnteprima {
  orderNumber: string;
  orderDate: string | null;
  deliveryDate: string | null;
  plant: string | null;
  addressId: number | null;
  addressLabel: string | null;
  giaPresente: boolean;
  existingOrderId: number | null;
  /** La consegna richiesta che ha adesso l'ordine che c'è già. */
  existingDelivery: string | null;
  /** Il foglio cambierebbe la consegna dell'ordine che c'è già. */
  deliveryWouldChange: boolean;
  /** Il cliente dell'ordine che c'è già, quando non è quello del foglio. */
  otherCustomer: {
    id: number;
    name: string;
    bcCode: string | null;
    state: string | null;
    orderDate: string | null;
  } | null;
  righe: RigaAnteprima[];
  problemi: ProblemaRiga[];
}

export interface Anteprima {
  orders: OrdineAnteprima[];
  rowsRead: number;
  rowsBlocked: number;
  ordersToCreate: number;
  ordersAlreadyThere: number;
  /** Ordini già presenti dello stesso cliente la cui consegna cambierebbe. */
  deliveriesToUpdate: number;
  /** I PO già di un altro cliente che il foglio cambierebbe: la domanda da fare prima. */
  otherCustomerOrders: string[];
  /** Righe che entrano ma con qualcosa da guardare. */
  rowsWarned: number;
  problems: ProblemaRiga[];
  /** Gli stabilimenti che non si sa dove mandare: la domanda da fare prima. */
  unknownPlants: { plant: string; rows: number }[];
  /** Gli articoli il cui prezzo nel foglio non è quello del listino. */
  priceMismatches: {
    articleCode: string;
    description: string | null;
    sheetPrice: number;
    listPrice: number;
    rows: number;
  }[];
}

export interface EsitoImportazione {
  created: { orderId: number; orderNumber: string; lines: number }[];
  /** Ordini che c'erano già: si è aggiornata la consegna richiesta. */
  updated: { orderId: number; orderNumber: string; from: string | null; to: string }[];
  skipped: { orderNumber: string; reason: string }[];
  linesSkipped: { orderNumber: string; row: number; reason: string }[];
}

export function useCampiMappabili() {
  return useQuery({
    queryKey: ['campi-import-excel'],
    queryFn: () => chiama<{ fields: CampoMappabile[] }>('/api/orders/import/fields'),
    staleTime: Infinity,
  });
}

/**
 * Il file va per conto suo, con un `FormData`: `chiama` mette
 * `content-type: application/json` e il confine del multipart lo deve
 * scrivere il browser.
 *
 * Il cliente si manda **prima** del file: il servizio legge le parti in
 * ordine, e un campo che arriva dopo il file arriverebbe troppo tardi.
 */
export function useCaricaFoglio() {
  return useMutation({
    mutationFn: async ({ file, customerId }: { file: File; customerId: number }) => {
      const dati = new FormData();
      dati.append('customerId', String(customerId));
      dati.append('file', file);
      const risposta = await fetch('/api/orders/import/upload', {
        method: 'POST',
        body: dati,
        credentials: 'same-origin',
      });
      const corpo = await risposta.json().catch(() => null);
      if (!risposta.ok) {
        throw new ErroreApi(
          corpo?.error ?? 'erroreCaricamento',
          corpo?.message ?? t('Il file non si è potuto leggere.'),
          risposta.status,
        );
      }
      return corpo as EsitoCaricamento;
    },
  });
}

export function useCambiaFoglio() {
  return useMutation({
    mutationFn: (dati: {
      token: string;
      sheet: string;
      headerRow: number;
      customerId: number;
    }) =>
      chiama<{ sheet: Foglio; mapping: Mappatura; missing: string[] }>(
        '/api/orders/import/sheet',
        { metodo: 'POST', corpo: dati },
      ),
  });
}

export interface RichiestaAnteprima {
  token: string;
  customerId: number;
  sheet: string;
  headerRow: number;
  mapping: Mappatura;
  pricelistId?: number | null;
  /** Stabilimento scritto nel foglio → destinazione, deciso da chi importa. */
  plantAddresses?: Record<string, number>;
  /** PO già di un altro cliente → cosa farne, deciso da chi importa. */
  otherCustomerChoices?: Record<string, SceltaAltroCliente>;
}

/** Lasciare com'è l'ordine dell'altro cliente, o aggiornargli la consegna. */
export type SceltaAltroCliente = 'skip' | 'update';

export function useAnteprimaImport() {
  return useMutation({
    mutationFn: (dati: RichiestaAnteprima) =>
      chiama<Anteprima>('/api/orders/import/preview', { metodo: 'POST', corpo: dati }),
  });
}

export function useImportaOrdini() {
  return useMutation({
    mutationFn: (dati: RichiestaAnteprima & { sectionalId: number; remember: boolean }) =>
      chiama<EsitoImportazione>('/api/orders/import/commit', { metodo: 'POST', corpo: dati }),
  });
}
